import { useState, useEffect, useCallback } from 'react';
import { getBackups, restoreBackup } from '../utils/api';
import { fetchProperties } from '../utils/api';

/**
 * 롤백 모달 컴포넌트
 */
function RollbackModal({ isOpen, onClose, onSuccess }) {
  const [backups, setBackups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [selected상권s, setSelected상권s] = useState([]);
  const [available상권s, setAvailable상권s] = useState([]);
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState(null);

  const loadBackups = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getBackups();
      
      // result가 배열이면 그대로 사용, 객체면 data 속성 사용
      const backups = Array.isArray(result) ? result : (result?.data || []);
      
      setBackups(backups);
      
      // 백업이 없을 때 서버 메시지 표시
      if (backups.length === 0 && result?.message) {
        setError(result.message + (result.hint ? `\n\n${result.hint}` : ''));
      }
    } catch (error) {
      console.error('백업 목록 로드 실패:', error);
      setError(error.message || '백업 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const load상권s = useCallback(async () => {
    try {
      const properties = await fetchProperties();
      const unique상권s = Array.from(new Set(properties.map(p => p?.상권).filter(Boolean))).sort();
      setAvailable상권s(unique상권s);
    } catch (error) {
      console.error('상권 목록 로드 실패:', error);
    }
  }, []);

  // 백업 목록 로드
  useEffect(() => {
    if (isOpen) {
      loadBackups();
      load상권s();
    }
  }, [isOpen, loadBackups, load상권s]);

  const handleBackupSelect = (backup) => {
    setSelectedBackup(backup);
    // 백업에 포함된 상권 중에서 선택 가능한 상권만 필터링
    const backup상권s = backup.상권s || [];
    const selectable상권s = backup상권s.filter(상권 => available상권s.includes(상권));
    setSelected상권s(selectable상권s);
    setError(null);
  };

  const handle상권Toggle = (상권) => {
    setSelected상권s(prev => {
      if (prev.includes(상권)) {
        return prev.filter(s => s !== 상권);
      } else {
        return [...prev, 상권];
      }
    });
  };

  const handleRestore = async () => {
    if (!selectedBackup) {
      setError('백업을 선택해주세요.');
      return;
    }

    if (selected상권s.length === 0) {
      setError('복원할 상권을 최소 1개 이상 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selected상권s.length}개 상권의 데이터를 복원하시겠습니까?\n\n복원 전 현재 상태는 자동으로 백업됩니다.`)) {
      return;
    }

    try {
      setIsRestoring(true);
      setError(null);
      
      const result = await restoreBackup(selectedBackup.jobId, selected상권s);
      
      alert(`복원 완료!\n\n${result.restored}개의 매물이 복원되었습니다.`);
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('복원 실패:', error);
      setError(error.message || '복원 중 오류가 발생했습니다.');
    } finally {
      setIsRestoring(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">데이터 롤백</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              disabled={isRestoring}
            >
              ×
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>백업 데이터가 없습니다.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* 백업 선택 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  백업 선택
                </label>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  {backups.map((backup) => (
                    <button
                      key={backup.id}
                      onClick={() => handleBackupSelect(backup)}
                      className={`w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        selectedBackup?.id === backup.id ? 'bg-orange-50 border-orange-200' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-800">
                            {formatDate(backup.createdAt)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            상권: {backup.상권s?.join(', ') || '(없음)'} | 
                            생성: {backup.changes?.created?.length || 0} | 
                            변경: {backup.changes?.updated?.length || 0} | 
                            삭제: {backup.changes?.deleted?.length || 0}
                          </div>
                        </div>
                        {selectedBackup?.id === backup.id && (
                          <span className="text-orange-500">✓</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* 상권 선택 */}
              {selectedBackup && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    복원할 상권 선택 (최소 1개)
                  </label>
                  <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {selectedBackup.상권s && selectedBackup.상권s.length > 0 ? (
                      <div className="space-y-2">
                        {selectedBackup.상권s.map((상권) => {
                          const isAvailable = available상권s.includes(상권);
                          const isSelected = selected상권s.includes(상권);
                          
                          return (
                            <label
                              key={상권}
                              className={`flex items-center p-2 rounded cursor-pointer transition-colors ${
                                isSelected ? 'bg-orange-50 border border-orange-200' : 'hover:bg-gray-50'
                              } ${!isAvailable ? 'opacity-50' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handle상권Toggle(상권)}
                                disabled={!isAvailable || isRestoring}
                                className="mr-2"
                              />
                              <span className="text-sm">
                                {상권}
                                {!isAvailable && <span className="text-gray-400 ml-1">(현재 없음)</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">백업에 상권 정보가 없습니다.</p>
                    )}
                  </div>
                </div>
              )}

              {/* 액션 버튼 */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
                  disabled={isRestoring}
                >
                  취소
                </button>
                <button
                  onClick={handleRestore}
                  disabled={!selectedBackup || selected상권s.length === 0 || isRestoring}
                  className="px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRestoring ? '복원 중...' : '복원하기'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RollbackModal;

